import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { 
  LogOut, Users, Calendar, Package, Clock, TrendingUp, 
  Building, FileText, MessageSquare, Plus, Search, Filter,
  ChevronLeft, ChevronRight, HelpCircle, Bell
} from 'lucide-react';
import { HelpCenter } from '@/components/HelpCenter';
import '@/styles/companyHeader.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function OperationsManagementDashboard({ user, setUser }) {
  // Data states
  const [employees, setEmployees] = useState([]);
  const [projects, setProjects] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [timeEntries, setTimeEntries] = useState([]);
  const [messages, setMessages] = useState([]);
  const [materialOrders, setMaterialOrders] = useState([]);
  
  // UI states
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [showHelpCenter, setShowHelpCenter] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Dialog states for stat cards
  const [showClockedInDialog, setShowClockedInDialog] = useState(false);
  const [showProjectsDialog, setShowProjectsDialog] = useState(false);
  const [showStaffDialog, setShowStaffDialog] = useState(false);
  const [showOrdersDialog, setShowOrdersDialog] = useState(false);
  const [showHoursDialog, setShowHoursDialog] = useState(false);
  
  // Stats
  const [stats, setStats] = useState({
    activeProjects: 0,
    clockedInNow: 0,
    totalEmployees: 0,
    pendingOrders: 0,
    weekHours: 0
  });

  // Fetch all data
  const fetchData = useCallback(async () => {
    const token = localStorage.getItem('token');
    try {
      const today = new Date().toISOString().split('T')[0];
      const weekStart = new Date();
      weekStart.setDate(weekStart.getDate() - weekStart.getDay());
      
      const [empRes, projRes, schedRes, timeRes, msgRes, ordersRes] = await Promise.all([
        fetch(`${API_URL}/api/employees`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API_URL}/api/projects`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API_URL}/api/schedules?start_date=${today}&end_date=${today}`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API_URL}/api/time/entries`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API_URL}/api/messages`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API_URL}/api/material-orders`, { headers: { 'Authorization': `Bearer ${token}` } })
      ]);

      if (empRes.ok) {
        const empData = await empRes.json();
        setEmployees(empData);
        
        // Count clocked in
        const clockedIn = empData.filter(e => e.is_clocked_in).length;
        setStats(prev => ({ ...prev, totalEmployees: empData.length, clockedInNow: clockedIn }));
      }
      
      if (projRes.ok) {
        const projData = await projRes.json();
        setProjects(projData);
        setStats(prev => ({ ...prev, activeProjects: projData.filter(p => p.is_active).length }));
      }
      
      if (schedRes.ok) setSchedules(await schedRes.json());
      if (timeRes.ok) {
        const entries = await timeRes.json();
        setTimeEntries(entries);
        
        // Calculate week hours
        const weekEntries = entries.filter(e => {
          const entryDate = new Date(e.clock_in);
          return entryDate >= weekStart;
        });
        const totalHours = weekEntries.reduce((sum, e) => {
          if (e.clock_out) {
            return sum + (new Date(e.clock_out) - new Date(e.clock_in)) / 3600000;
          }
          return sum;
        }, 0);
        setStats(prev => ({ ...prev, weekHours: Math.round(totalHours) }));
      }
      if (msgRes.ok) setMessages(await msgRes.json());
      if (ordersRes.ok) {
        const orders = await ordersRes.json();
        setMaterialOrders(orders);
        setStats(prev => ({ ...prev, pendingOrders: orders.filter(o => o.status !== 'completed').length }));
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 60000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    setUser(null);
  };

  const getRoleName = (role) => {
    const roleNames = {
      'director': 'Director',
      'general_manager': 'General Manager',
      'senior_manager': 'Senior Manager',
      'manager': 'Manager',
      'project_manager': 'Project Manager'
    };
    return roleNames[role] || role.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="operations-dashboard">
      {/* Header */}
      <header className="bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Operations Management</h1>
            <p className="text-sm text-gray-500">{getRoleName(user.role)} • {user.name}</p>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => setShowHelpCenter(true)}>
              <HelpCircle className="w-5 h-5" />
            </Button>
            <Button variant="ghost" size="icon">
              <Bell className="w-5 h-5" />
            </Button>
            <Button variant="outline" onClick={handleLogout}>
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          <Card 
            className="bg-gradient-to-br from-blue-500 to-blue-600 text-white cursor-pointer hover:shadow-lg transition-shadow"
            onClick={() => setShowProjectsDialog(true)}
          >
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <Building className="w-8 h-8 opacity-80" />
                <div>
                  <p className="text-sm opacity-90">Active Projects</p>
                  <p className="text-2xl font-bold">{stats.activeProjects}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card 
            className="bg-gradient-to-br from-green-500 to-green-600 text-white cursor-pointer hover:shadow-lg transition-shadow"
            onClick={() => setShowClockedInDialog(true)}
          >
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <Clock className="w-8 h-8 opacity-80" />
                <div>
                  <p className="text-sm opacity-90">Clocked In Now</p>
                  <p className="text-2xl font-bold">{stats.clockedInNow}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card 
            className="bg-gradient-to-br from-purple-500 to-purple-600 text-white cursor-pointer hover:shadow-lg transition-shadow"
            onClick={() => setShowStaffDialog(true)}
          >
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <Users className="w-8 h-8 opacity-80" />
                <div>
                  <p className="text-sm opacity-90">Total Staff</p>
                  <p className="text-2xl font-bold">{stats.totalEmployees}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card 
            className="bg-gradient-to-br from-orange-500 to-orange-600 text-white cursor-pointer hover:shadow-lg transition-shadow"
            onClick={() => setShowOrdersDialog(true)}
          >
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <Package className="w-8 h-8 opacity-80" />
                <div>
                  <p className="text-sm opacity-90">Pending Orders</p>
                  <p className="text-2xl font-bold">{stats.pendingOrders}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card 
            className="bg-gradient-to-br from-indigo-500 to-indigo-600 text-white cursor-pointer hover:shadow-lg transition-shadow"
            onClick={() => setShowHoursDialog(true)}
          >
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <TrendingUp className="w-8 h-8 opacity-80" />
                <div>
                  <p className="text-sm opacity-90">Week Hours</p>
                  <p className="text-2xl font-bold">{stats.weekHours}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="grid grid-cols-5 w-full md:w-auto md:inline-flex">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="employees">Employees</TabsTrigger>
            <TabsTrigger value="projects">Projects</TabsTrigger>
            <TabsTrigger value="schedule">Schedule</TabsTrigger>
            <TabsTrigger value="orders">Orders</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Today's Schedule */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Calendar className="w-5 h-5" />
                    Today's Schedule
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3 max-h-64 overflow-y-auto">
                    {schedules.length > 0 ? (
                      schedules.slice(0, 5).map((schedule, idx) => (
                        <div key={idx} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                          <div>
                            <p className="font-medium">{schedule.project_name}</p>
                            <p className="text-sm text-gray-500">
                              {schedule.team_details?.length || 0} team members
                            </p>
                          </div>
                          <Badge>{schedule.status || 'scheduled'}</Badge>
                        </div>
                      ))
                    ) : (
                      <p className="text-center text-gray-500 py-4">No schedules for today</p>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Recent Activity */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Clock className="w-5 h-5" />
                    Recent Time Activity
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3 max-h-64 overflow-y-auto">
                    {timeEntries.slice(0, 5).map((entry, idx) => (
                      <div key={idx} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                        <div>
                          <p className="font-medium">{entry.employee_name || 'Employee'}</p>
                          <p className="text-sm text-gray-500">
                            {new Date(entry.clock_in).toLocaleTimeString()}
                            {entry.clock_out && ` - ${new Date(entry.clock_out).toLocaleTimeString()}`}
                          </p>
                        </div>
                        <Badge variant={entry.clock_out ? 'secondary' : 'default'}>
                          {entry.clock_out ? 'Completed' : 'Active'}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Recent Messages */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MessageSquare className="w-5 h-5" />
                  Recent Messages
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 max-h-48 overflow-y-auto">
                  {messages.slice(0, 5).map((msg, idx) => (
                    <div key={idx} className="p-3 bg-gray-50 rounded-lg">
                      <div className="flex justify-between items-start">
                        <p className="font-medium">{msg.sender_name}</p>
                        <span className="text-xs text-gray-500">
                          {new Date(msg.created_at).toLocaleDateString()}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mt-1 line-clamp-2">{msg.content}</p>
                    </div>
                  ))}
                  {messages.length === 0 && (
                    <p className="text-center text-gray-500 py-4">No recent messages</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Employees Tab */}
          <TabsContent value="employees">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  Team Members
                </CardTitle>
                <div className="flex items-center gap-2">
                  <div className="relative">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <Input 
                      placeholder="Search..." 
                      className="pl-9 w-64"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-3 px-4">Name</th>
                        <th className="text-left py-3 px-4">Position</th>
                        <th className="text-left py-3 px-4">Status</th>
                        <th className="text-left py-3 px-4">Today's Hours</th>
                      </tr>
                    </thead>
                    <tbody>
                      {employees
                        .filter(emp => 
                          emp.user?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          emp.position?.toLowerCase().includes(searchTerm.toLowerCase())
                        )
                        .slice(0, 20)
                        .map((emp, idx) => (
                          <tr key={idx} className="border-b hover:bg-gray-50">
                            <td className="py-3 px-4">
                              <div>
                                <p className="font-medium">{emp.user?.name || 'N/A'}</p>
                                <p className="text-sm text-gray-500">{emp.user?.email}</p>
                              </div>
                            </td>
                            <td className="py-3 px-4">{emp.position || '-'}</td>
                            <td className="py-3 px-4">
                              <Badge variant={emp.is_clocked_in ? 'default' : 'secondary'}>
                                {emp.is_clocked_in ? 'Clocked In' : 'Clocked Out'}
                              </Badge>
                            </td>
                            <td className="py-3 px-4">{emp.today_hours?.toFixed(2) || '0.00'} hrs</td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Projects Tab */}
          <TabsContent value="projects">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building className="w-5 h-5" />
                  All Projects
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {projects.filter(p => p.is_active).map((project, idx) => (
                    <Card key={idx} className="border hover:shadow-md transition-shadow">
                      <CardContent className="pt-4">
                        <h3 className="font-semibold text-lg">{project.name}</h3>
                        {project.address && (
                          <p className="text-sm text-gray-500 mt-1">{project.address}</p>
                        )}
                        <div className="flex gap-2 mt-3">
                          <Badge variant={project.is_active ? 'default' : 'secondary'}>
                            {project.is_active ? 'Active' : 'Inactive'}
                          </Badge>
                          {project.priority && (
                            <Badge variant="outline">{project.priority}</Badge>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Schedule Tab */}
          <TabsContent value="schedule">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="w-5 h-5" />
                  Schedule Overview
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {schedules.map((schedule, idx) => (
                    <div key={idx} className="p-4 border rounded-lg hover:bg-gray-50">
                      <div className="flex justify-between items-start">
                        <div>
                          <h4 className="font-semibold">{schedule.project_name}</h4>
                          <p className="text-sm text-gray-500">
                            {new Date(schedule.date).toLocaleDateString()}
                            {schedule.start_time && ` • ${schedule.start_time} - ${schedule.end_time}`}
                          </p>
                          <p className="text-sm text-gray-600 mt-1">
                            Team: {schedule.team_details?.map(t => t.name).join(', ') || 'Not assigned'}
                          </p>
                        </div>
                        <Badge>{schedule.status || 'scheduled'}</Badge>
                      </div>
                    </div>
                  ))}
                  {schedules.length === 0 && (
                    <p className="text-center text-gray-500 py-8">No schedules available</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Orders Tab */}
          <TabsContent value="orders">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Package className="w-5 h-5" />
                  Material Orders
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {materialOrders.slice(0, 20).map((order, idx) => (
                    <div key={idx} className="p-4 border rounded-lg hover:bg-gray-50">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-medium">{order.project_name || 'Unknown Project'}</p>
                          <p className="text-sm text-gray-500">
                            Created: {new Date(order.created_at).toLocaleDateString()}
                          </p>
                          {order.items && (
                            <p className="text-sm text-gray-600 mt-1">
                              {order.items.length} item(s)
                            </p>
                          )}
                        </div>
                        <Badge variant={
                          order.status === 'completed' ? 'default' :
                          order.status === 'in_progress' ? 'secondary' :
                          'outline'
                        }>
                          {order.status}
                        </Badge>
                      </div>
                    </div>
                  ))}
                  {materialOrders.length === 0 && (
                    <p className="text-center text-gray-500 py-8">No material orders</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* Help Center */}
      <HelpCenter open={showHelpCenter} onOpenChange={setShowHelpCenter} userRole={user.role} />

      {/* Clocked In Dialog */}
      <Dialog open={showClockedInDialog} onOpenChange={setShowClockedInDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-green-600" />
              Currently Clocked In ({employees.filter(e => e.is_clocked_in).length})
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            {employees.filter(e => e.is_clocked_in).map(emp => (
              <div key={emp.id} className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-200">
                <div>
                  <p className="font-medium">{emp.full_name}</p>
                  <p className="text-sm text-gray-600">{emp.role || 'Employee'}</p>
                </div>
                <Badge variant="default" className="bg-green-600">Working</Badge>
              </div>
            ))}
            {employees.filter(e => e.is_clocked_in).length === 0 && (
              <p className="text-center text-gray-500 py-8">No one is currently clocked in</p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Active Projects Dialog */}
      <Dialog open={showProjectsDialog} onOpenChange={setShowProjectsDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building className="w-5 h-5 text-blue-600" />
              Active Projects ({projects.filter(p => p.is_active).length})
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            {projects.filter(p => p.is_active).map(proj => (
              <div key={proj.id} className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-200">
                <div>
                  <p className="font-medium">{proj.name}</p>
                  <p className="text-sm text-gray-600">{proj.address}</p>
                </div>
                <Badge variant={proj.stage === 'pre_construction' ? 'secondary' : 'default'}>
                  {proj.stage === 'pre_construction' ? '📋 Pre-Con' : '🏗️ Construction'}
                </Badge>
              </div>
            ))}
            {projects.filter(p => p.is_active).length === 0 && (
              <p className="text-center text-gray-500 py-8">No active projects</p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Total Staff Dialog */}
      <Dialog open={showStaffDialog} onOpenChange={setShowStaffDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-purple-600" />
              All Staff ({employees.length})
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            {employees.map(emp => (
              <div key={emp.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border">
                <div>
                  <p className="font-medium">{emp.full_name}</p>
                  <p className="text-sm text-gray-600">{emp.email}</p>
                </div>
                <Badge variant={emp.is_clocked_in ? 'default' : 'secondary'}>
                  {emp.is_clocked_in ? 'Clocked In' : 'Clocked Out'}
                </Badge>
              </div>
            ))}
            {employees.length === 0 && (
              <p className="text-center text-gray-500 py-8">No staff found</p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Pending Orders Dialog */}
      <Dialog open={showOrdersDialog} onOpenChange={setShowOrdersDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="w-5 h-5 text-orange-600" />
              Pending Orders ({materialOrders.filter(o => o.status !== 'completed').length})
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            {materialOrders.filter(o => o.status !== 'completed').map(order => (
              <div key={order.id} className="flex items-center justify-between p-3 bg-orange-50 rounded-lg border border-orange-200">
                <div>
                  <p className="font-medium">{order.project_name || 'Unknown Project'}</p>
                  <p className="text-sm text-gray-600">{order.items?.length || 0} items</p>
                </div>
                <Badge variant="outline" className="text-orange-600 border-orange-300">
                  {order.status}
                </Badge>
              </div>
            ))}
            {materialOrders.filter(o => o.status !== 'completed').length === 0 && (
              <p className="text-center text-gray-500 py-8">No pending orders</p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Week Hours Dialog */}
      <Dialog open={showHoursDialog} onOpenChange={setShowHoursDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-indigo-600" />
              This Week's Hours: {stats.weekHours}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            {employees.filter(e => e.week_hours > 0).sort((a, b) => (b.week_hours || 0) - (a.week_hours || 0)).map(emp => (
              <div key={emp.id} className="flex items-center justify-between p-3 bg-indigo-50 rounded-lg border border-indigo-200">
                <div>
                  <p className="font-medium">{emp.full_name}</p>
                  <p className="text-sm text-gray-600">{emp.role || 'Employee'}</p>
                </div>
                <Badge variant="secondary">{emp.week_hours || 0} hrs</Badge>
              </div>
            ))}
            {employees.filter(e => e.week_hours > 0).length === 0 && (
              <p className="text-center text-gray-500 py-8">No hours logged this week</p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
